import { IMPORT_PICKER_URL } from '@/config'
import { failingPickedFile } from '@/modules/upload/cloud/CloudDownloadError'
import type { CloudSource, PickedCloudFile } from '@/modules/upload/cloud/types'
import {
  APP_MESSAGE_SOURCE,
  isPickerMessage,
  isTransferred,
  type PickerFailure,
  type PickerFileMessage,
  type PickerMessage,
  type TicketMessage,
} from './pickerMessages'

interface PickerPageSourceOptions {
  provider: string
  label: string
  blockedLinkAdvice?: string
  // Called when the picker page says ready: a ticket from lukita that lets the picker finish one sign-in.
  requestTicket: () => Promise<string>
  pickerUrl?: string
  closedCheckMilliseconds?: number
}

// Never a named window: window.open reuses a window of that name, and a stale or planted picker page could answer.
const PICKER_WINDOW_TARGET = '_blank'
const PICKER_WINDOW_FEATURES = 'popup,width=840,height=720'
const DEFAULT_CLOSED_CHECK_MILLISECONDS = 500
const CLOSED_CHECKS_BEFORE_GIVING_UP = 2

export type PickerPageFailure =
  | PickerFailure
  | 'popup-blocked'
  | 'ticket-failed'

export class PickerPageError extends Error {
  readonly failure: PickerPageFailure
  // Kept by hand: this app's TypeScript target predates Error's own cause option.
  readonly cause?: unknown

  constructor(failure: PickerPageFailure, cause?: unknown) {
    super(`Import picker failed: ${failure}`)
    this.name = 'PickerPageError'
    this.failure = failure
    this.cause = cause
  }
}

const originOf = (url: string | undefined) => {
  if (!url) return null
  try {
    return new URL(url).origin
  } catch {
    return null
  }
}

const pickerPageUrl = (
  pickerOrigin: string,
  provider: string,
  requestId: string,
) => {
  const url = new URL('/', pickerOrigin)
  url.searchParams.set('provider', provider)
  url.searchParams.set('returnTo', window.location.origin)
  url.searchParams.set('requestId', requestId)
  return url.toString()
}

const toPickedFile = (file: PickerFileMessage): PickedCloudFile => {
  if (!isTransferred(file)) return failingPickedFile(file, file.failure)
  return {
    name: file.name,
    size: file.size,
    download: async () => new Blob([file.bytes]),
  }
}

interface PickSettlers {
  resolve: (files: PickedCloudFile[]) => void
  reject: (error: Error) => void
}

// Files come one batch at a time and are kept until done. Returns true once the pick is over.
const takeMessage = (
  message: PickerMessage,
  collected: PickedCloudFile[],
  settlers: PickSettlers,
) => {
  if (message.type === 'ready') return false
  if (message.type === 'files') {
    collected.push(...message.files.map(toPickedFile))
    return false
  }
  if (message.type === 'failed') {
    settlers.reject(new PickerPageError(message.failure))
    return true
  }
  settlers.resolve(collected)
  return true
}

interface PopupWatch {
  popup: Window
  pickerOrigin: string
  requestId: string
  closedCheckMilliseconds: number
  requestTicket: () => Promise<string>
}

// The ticket goes only to the popup, at the picker origin. If lukita gives none, the popup is closed.
const sendTicket = async (
  watch: PopupWatch,
  onTicketFailure: (error: Error) => void,
) => {
  try {
    const ticket = await watch.requestTicket()
    const message: TicketMessage = {
      source: APP_MESSAGE_SOURCE,
      type: 'ticket',
      requestId: watch.requestId,
      ticket,
    }
    watch.popup.postMessage(message, watch.pickerOrigin)
  } catch (error) {
    watch.popup.close()
    onTicketFailure(new PickerPageError('ticket-failed', error))
  }
}

const isFromThisPick = (event: MessageEvent, watch: PopupWatch) =>
  event.origin === watch.pickerOrigin &&
  event.source === watch.popup &&
  isPickerMessage(event.data) &&
  event.data.requestId === watch.requestId

// The popup posts its last batch and then closes, so one closed check is not enough: a late message still wins.
// A popup closed mid-pick still hands over the files that already came.
const watchPopup = (watch: PopupWatch, settlers: PickSettlers) => {
  const collected: PickedCloudFile[] = []
  let closedChecksInARow = 0
  let hasSentTicket = false
  const stopWatching = () => {
    window.removeEventListener('message', onMessage)
    window.clearInterval(closedCheck)
  }
  const failPick = (error: Error) => {
    stopWatching()
    settlers.reject(error)
  }
  // Answered once: a second ready would spend a second ticket.
  const answerReady = () => {
    if (hasSentTicket) return
    hasSentTicket = true
    sendTicket(watch, failPick)
  }
  const onMessage = (event: MessageEvent) => {
    if (!isFromThisPick(event, watch)) return
    if (event.data.type === 'ready') answerReady()
    else if (takeMessage(event.data, collected, settlers)) stopWatching()
  }
  const checkClosed = () => {
    closedChecksInARow = watch.popup.closed ? closedChecksInARow + 1 : 0
    if (closedChecksInARow < CLOSED_CHECKS_BEFORE_GIVING_UP) return
    stopWatching()
    settlers.resolve(collected)
  }
  const closedCheck = window.setInterval(
    checkClosed,
    watch.closedCheckMilliseconds,
  )
  window.addEventListener('message', onMessage)
}

// window.open runs inside the click that called pick, or the browser blocks the popup.
const pickThroughPopup = (
  pickerOrigin: string,
  options: PickerPageSourceOptions,
) =>
  new Promise<PickedCloudFile[]>((resolve, reject) => {
    const requestId = crypto.randomUUID()
    const popup = window.open(
      pickerPageUrl(pickerOrigin, options.provider, requestId),
      PICKER_WINDOW_TARGET,
      PICKER_WINDOW_FEATURES,
    )
    if (!popup) {
      reject(new PickerPageError('popup-blocked'))
      return
    }
    const closedCheckMilliseconds =
      options.closedCheckMilliseconds ?? DEFAULT_CLOSED_CHECK_MILLISECONDS
    const { requestTicket } = options
    watchPopup(
      {
        popup,
        pickerOrigin,
        requestId,
        closedCheckMilliseconds,
        requestTicket,
      },
      { resolve, reject },
    )
  })

const configuredOrigin = (pickerOrigin: string | null) => {
  if (!pickerOrigin) throw new Error('Import picker URL is not set')
  return pickerOrigin
}

// Box and Microsoft sign in on a separate picker page, so their tokens never share a page with the CounselPro login.
export const createPickerPageSource = (
  options: PickerPageSourceOptions,
): CloudSource => {
  const pickerOrigin = originOf(options.pickerUrl ?? IMPORT_PICKER_URL)
  return {
    label: options.label,
    blockedLinkAdvice: options.blockedLinkAdvice,
    isAvailable: () => pickerOrigin !== null,
    pick: () => pickThroughPopup(configuredOrigin(pickerOrigin), options),
  }
}
